import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Eye, Shield, FileSearch, Users, CheckCircle, ArrowRight } from 'lucide-react';

const Services = () => {
  const { t, i18n } = useTranslation();
  
  const services = [
    {
      icon: <Search className="w-12 h-12" />,
      title: t('services.pentest.title'),
      shortDesc: t('services.pentest.short'),
      description: t('services.pentest.desc'),
      features: [
        t('services.pentest.feat1'),
        t('services.pentest.feat2'),
        t('services.pentest.feat3'),
        t('services.pentest.feat4'),
        t('services.pentest.feat5'),
        t('services.pentest.feat6'),
      ],
      link: '/penetration-testing',
    },
    {
      icon: <Eye className="w-12 h-12" />,
      title: t('services.siem.title'),
      shortDesc: t('services.siem.short'),
      description: t('services.siem.desc'),
      features: [
        t('services.siem.feat1'),
        t('services.siem.feat2'),
        t('services.siem.feat3'),
        t('services.siem.feat4'),
        t('services.siem.feat5'),
        t('services.siem.feat6'),
      ],
      link: '/contact',
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: t('services.idsips.title'),
      shortDesc: t('services.idsips.short'),
      description: t('services.idsips.desc'),
      features: [
        t('services.idsips.feat1'),
        t('services.idsips.feat2'),
        t('services.idsips.feat3'),
        t('services.idsips.feat4'),
        t('services.idsips.feat5'),
        t('services.idsips.feat6'),
      ],
      link: '/contact',
    },
    {
      icon: <FileSearch className="w-12 h-12" />,
      title: t('services.vuln.title'),
      shortDesc: t('services.vuln.short'),
      description: t('services.vuln.desc'),
      features: [
        t('services.vuln.feat1'),
        t('services.vuln.feat2'),
        t('services.vuln.feat3'),
        t('services.vuln.feat4'),
        t('services.vuln.feat5'),
        t('services.vuln.feat6'),
      ],
      link: '/contact',
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: t('services.training.title'),
      shortDesc: t('services.training.short'),
      description: t('services.training.desc'),
      features: [
        t('services.training.feat1'),
        t('services.training.feat2'),
        t('services.training.feat3'),
        t('services.training.feat4'),
        t('services.training.feat5'),
        t('services.training.feat6'),
      ],
      link: '/contact',
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: t('services.malware.title'),
      shortDesc: t('services.malware.short'),
      description: t('services.malware.desc'),
      features: [
        t('services.malware.feat1'),
        t('services.malware.feat2'),
        t('services.malware.feat3'),
        t('services.malware.feat4'),
        t('services.malware.feat5'),
        t('services.malware.feat6'),
      ],
      link: '/contact',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <Helmet>
        <title>{i18n.language === 'tr' ? 'Siber Güvenlik Hizmetleri | SULH - Penetrasyon Testi, SIEM, Malware Analizi' : 'Cybersecurity Services | SULH - Penetration Testing, SIEM, Malware Analysis'}</title>
        <meta name="description" content={i18n.language === 'tr' ? 'Kapsamlı siber güvenlik hizmetleri: Penetrasyon testi, SIEM, IDS/IPS, zafiyet analizi, güvenlik eğitimi ve malware tersine mühendislik. ISO 27001 sertifikalı.' : 'Comprehensive cybersecurity services: Penetration testing, SIEM, IDS/IPS, vulnerability assessment, security training, and malware reverse engineering. ISO 27001 certified.'} />
        <meta name="keywords" content="siber güvenlik hizmetleri, penetrasyon testi, SIEM, IDS IPS, zafiyet analizi, güvenlik eğitimi, malware analizi, reverse engineering" />
        <link rel="canonical" href="https://www.sulh.tech/services" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-blue-900/20 via-transparent to-cyan-900/20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="services-title">
            {t('services.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto" data-testid="services-subtitle">
            {t('services.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding" data-testid="services-list">
        <div className="max-w-7xl mx-auto space-y-16">
          {services.map((service, index) => (
            <div
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
              data-testid={`service-detail-${index}`}
            >
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="glass-card p-8">
                  <div className="text-blue-400 mb-6">{service.icon}</div>
                  <h2 className="text-3xl font-bold text-white mb-4">{service.title}</h2>
                  <p className="text-gray-300 mb-6">{service.description}</p>
                  
                  {service.link === '/penetration-testing' ? (
                    <Link to={service.link}>
                      <button className="btn-primary flex items-center gap-2" data-testid={`service-cta-${index}`}>
                        {t('services.learnMore')}
                        <ArrowRight size={18} />
                      </button>
                    </Link>
                  ) : (
                    <Link to={service.link}>
                      <button className="btn-primary flex items-center gap-2" data-testid={`service-cta-${index}`}>
                        {t('services.getStarted')}
                        <ArrowRight size={18} />
                      </button>
                    </Link>
                  )}
                </div>
              </div>
              
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-white mb-4">{t('services.keyFeatures')}</h3>
                  {service.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 text-gray-300"
                      data-testid={`service-feature-${index}-${idx}`}
                    >
                      <CheckCircle className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-slate-900/50" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center">
            {t('services.why.title')}
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="glass-card p-8 text-center" data-testid="why-card-0">
              <div className="text-4xl font-bold text-blue-400 mb-4">15+</div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('services.why.experience')}</h3>
              <p className="text-gray-400">{t('services.why.experienceDesc')}</p>
            </div>
            <div className="glass-card p-8 text-center" data-testid="why-card-1">
              <div className="text-4xl font-bold text-blue-400 mb-4">ISO 27001</div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('services.why.certified')}</h3>
              <p className="text-gray-400">{t('services.why.certifiedDesc')}</p>
            </div>
            <div className="glass-card p-8 text-center" data-testid="why-card-2">
              <div className="text-4xl font-bold text-blue-400 mb-4">TSE</div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('services.why.tse')}</h3>
              <p className="text-gray-400">{t('services.why.tseDesc')}</p>
            </div>
            <div className="glass-card p-8 text-center" data-testid="why-card-3">
              <div className="text-4xl font-bold text-blue-400 mb-4">24/7</div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('services.why.support')}</h3>
              <p className="text-gray-400">{t('services.why.supportDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="services-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('services.cta.title')}
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            {t('services.cta.subtitle')}
          </p>
          <Link to="/contact">
            <button className="btn-primary text-lg" data-testid="services-contact-button">
              {t('services.cta.button')}
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
